import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, User } from "lucide-react";
import { api } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";

function PlayerAvatar({ player }) {
    const [broken, setBroken] = useState(false);
    if (player.photo_url && !broken) {
        return (
            <img
                src={player.photo_url}
                alt={player.name}
                onError={() => setBroken(true)}
                className="w-full h-52 object-contain bg-[#101018]"
            />
        );
    }
    return (
        <div className="w-full h-52 bg-[#1A1A28] flex items-center justify-center">
            <User className="w-16 h-16 text-slate-600" />
        </div>
    );
}

export default function TeamDetail() {
    const { teamId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/teams/${teamId}`).then((r) => setData(r.data)).catch(() => setError(true));
    }, [teamId]);

    if (error)
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 text-center">
                <p className="font-display font-bold uppercase text-2xl text-slate-300">Team not found</p>
                <Link to="/teams" className="text-[#FF5500] text-sm mt-3 inline-block">
                    Back to teams
                </Link>
            </div>
        );
    if (!data) return <p className="max-w-7xl mx-auto px-4 sm:px-6 py-24 text-slate-500 font-stat text-sm">Loading…</p>;

    const { team, players } = data;
    const rec = team.record;

    return (
        <div data-testid="team-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <Link
                to="/teams"
                data-testid="back-to-teams"
                className="inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-slate-400 hover:text-[#FF5500] transition-colors duration-200"
            >
                <ArrowLeft className="w-4 h-4" /> All teams
            </Link>

            <div className="mt-8 card-arena rounded-3xl p-8 sm:p-10 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ background: `radial-gradient(ellipse at top right, ${team.primary_color}55, transparent 60%)` }}
                />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-8">
                    <TeamCrest team={team} size="xl" testid="team-detail-crest" />
                    <div className="flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FFB800]">{team.city}</p>
                        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none">
                            {team.name}
                        </h1>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-xs text-slate-400">
                            {team.venue && (
                                <span className="flex items-center gap-1.5">
                                    <MapPin className="w-3.5 h-3.5 text-[#FF5500]" /> {team.venue}
                                </span>
                            )}
                        </div>
                    </div>
                    {rec && (
                        <div data-testid="team-record" className="flex gap-6">
                            {[
                                { l: "Rank", v: `#${rec.rank}` },
                                { l: "Wins", v: rec.w },
                                { l: "Losses", v: rec.l },
                                { l: "Streak", v: rec.streak },
                            ].map((s) => (
                                <div key={s.l} className="text-center">
                                    <div className="font-stat font-bold text-2xl text-[#FF5500]">{s.v}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <h2 className="font-display font-black uppercase text-3xl text-white mt-14 mb-8">
                Roster <span className="text-stroke-faint">({players.length})</span>
            </h2>
            {players.length === 0 && (team.staff || []).length === 0 ? (
                <div data-testid="roster-empty" className="card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-xl text-slate-300">No players on the roster</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                    {(team.staff || []).map((s, i) => (
                        <motion.div
                            key={`staff-${i}`}
                            data-testid={`staff-card-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: i * 0.05 }}
                            className="card-arena rounded-2xl overflow-hidden"
                        >
                            <div className="relative">
                                {s.photo_url ? (
                                    <img src={s.photo_url} alt={s.name} className="w-full h-52 object-contain bg-[#101018]" />
                                ) : (
                                    <div className="w-full h-52 bg-[#1A1A28] flex items-center justify-center">
                                        <User className="w-16 h-16 text-slate-600" />
                                    </div>
                                )}
                                <span className="absolute top-3 left-3 font-display font-bold text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#08080C]/85 backdrop-blur-sm text-[#FFB800]">
                                    {s.role}
                                </span>
                            </div>
                            <div className="p-4">
                                <p className="font-display font-bold uppercase text-lg text-white leading-tight">{s.name}</p>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                                    {team.short_name} • {s.role}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                    {players.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={`player-card-${p.id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: i * 0.05 }}
                            className="card-arena rounded-2xl overflow-hidden"
                        >
                            <div className="relative">
                                <PlayerAvatar player={p} />
                                <span
                                    className="absolute top-3 left-3 font-stat font-bold text-sm px-2.5 py-1 rounded-lg bg-[#08080C]/85 backdrop-blur-sm"
                                    style={{ color: team.primary_color, border: `1px solid ${team.primary_color}66` }}
                                >
                                    #{p.number}
                                </span>
                                <span className="absolute top-3 right-3 font-display font-bold text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#08080C]/85 backdrop-blur-sm text-[#FFB800]">
                                    {p.position}
                                </span>
                            </div>
                            <div className="p-4">
                                <p className="font-display font-bold uppercase text-lg text-white leading-tight">
                                    {p.name}
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                                    {team.short_name} • {p.position}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
